import React, { useState, useEffect, useCallback, useRef } from 'react'
import { apiGet, apiPost, apiUploadFile } from '../../utils/api'
import { BrowseTab } from './BrowseTab'
import { VectorSearchTab } from './VectorSearchTab'

export interface ZohoChat {
  id: number
  chat_number: number
  visitor_name: string | null
  visitor_email: string | null
  department: string | null
  operator: string | null
  website: string | null
  os: string | null
  browser: string | null
  device: string | null
  city: string | null
  state: string | null
  country: string | null
  chat_date: string | null
  first_message: string | null
  message_count: number
  status: string
  pdf_filename: string | null
  pdf_path: string | null
  embedded: number
  created_at: string
}

export interface ZohoStats {
  total_chats: number
  total_messages: number
  embedded_chats: number
  pending_chats: number
  total_operators: number
  total_departments: number
  total_countries: number
  linked_chats: number
  unlinked_chats: number
  departments: Record<string, number>
  countries: Record<string, number>
}

type TabId = 'browse' | 'vector-search'

export const ZohoChatsView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('browse')
  const [stats, setStats] = useState<ZohoStats | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [scanResult, setScanResult] = useState<string | null>(null)
  const [uploadResult, setUploadResult] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<ZohoStats>('/zoho-chats/stats')
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch Zoho stats:', err)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleScan = async () => {
    setIsScanning(true)
    setScanResult(null)
    try {
      const result = await apiPost<{
        message: string
        scanned: number
        imported: number
        skipped: number
        errors: number
        errorDetails?: string[]
      }>('/zoho-chats/scan')
      setScanResult(`Imported ${result.imported} new chats (${result.skipped} skipped, ${result.errors} errors)`)
      if (result.imported > 0) fetchStats()
    } catch (err) {
      setScanResult(`Scan failed: ${(err as Error).message}`)
    } finally {
      setIsScanning(false)
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset the input so the same file can be re-selected
    e.target.value = ''

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setUploadResult('Error: Only PDF files are accepted')
      return
    }

    if (!file.name.match(/Chat #\d+/i)) {
      setUploadResult('Error: Filename must contain "Chat #NNN" (e.g. "Chat #123.pdf")')
      return
    }

    setIsUploading(true)
    setUploadResult(null)
    try {
      const result = await apiUploadFile<{
        message: string
        chatNumber: number
        visitorName: string
        department: string
        messageCount: number
      }>('/zoho-chats/upload', file)
      setUploadResult(`Chat #${result.chatNumber} imported (${result.visitorName}, ${result.messageCount} messages)`)
      fetchStats()
    } catch (err) {
      setUploadResult(`Upload failed: ${(err as Error).message}`)
    } finally {
      setIsUploading(false)
    }
  }

  // Auto-clear results after 8 seconds
  useEffect(() => {
    if (scanResult) {
      const t = setTimeout(() => setScanResult(null), 8000)
      return () => clearTimeout(t)
    }
  }, [scanResult])

  useEffect(() => {
    if (uploadResult) {
      const t = setTimeout(() => setUploadResult(null), 8000)
      return () => clearTimeout(t)
    }
  }, [uploadResult])

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    {
      id: 'browse',
      label: 'Browse Chats',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
    },
    {
      id: 'vector-search',
      label: 'Vector Search',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      ),
    },
  ]

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-surface-primary">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-txt-primary dark:text-white flex items-center gap-2">
              <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
              Zoho Chats Archive
            </h1>
            <p className="text-sm text-txt-secondary mt-0.5">
              Browse and search {stats?.total_chats?.toLocaleString() || '...'} archived chat transcripts from Zoho SalesIQ
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Status messages */}
            {(scanResult || uploadResult) && (
              <span className={`text-xs px-3 py-1.5 rounded-lg ${
                (scanResult || uploadResult || '').startsWith('Error') || (scanResult || uploadResult || '').includes('failed')
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {scanResult || uploadResult}
              </span>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="hidden"
            />

            {/* Upload single PDF button */}
            <button
              onClick={handleUploadClick}
              disabled={isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border bg-white text-txt-primary hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Importing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  Upload PDF
                </>
              )}
            </button>

            {/* Scan & import button */}
            <button
              onClick={handleScan}
              disabled={isScanning}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isScanning ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Scanning...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                  </svg>
                  Scan &amp; Import PDFs
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats row — 6 columns matching WhatsApp Archive style */}
        <div className="mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem' }}>
          {/* Total Chats */}
          <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-emerald-700 leading-tight">
                {stats?.total_chats?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-emerald-600/70 font-medium">Chats</div>
            </div>
          </div>

          {/* Total Messages */}
          <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-blue-700 leading-tight">
                {stats?.total_messages?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-blue-600/70 font-medium">Messages</div>
            </div>
          </div>

          {/* Departments */}
          <div className="rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-violet-700 leading-tight">
                {stats?.total_departments?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-violet-600/70 font-medium">Departments</div>
            </div>
          </div>

          {/* Countries */}
          <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-amber-700 leading-tight">
                {stats?.total_countries?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-amber-600/70 font-medium">Countries</div>
            </div>
          </div>

          {/* Linked to Customers */}
          <div className="rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.052a4.5 4.5 0 00-6.364-6.364L4.757 8.257a4.5 4.5 0 006.364 6.364l4.5-4.5z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-teal-700 leading-tight">
                {stats?.linked_chats?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-teal-600/70 font-medium">Linked</div>
            </div>
          </div>

          {/* Unlinked */}
          <div className="rounded-xl bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.181 8.68a4.503 4.503 0 011.903 6.405m-9.768-2.782L3.56 14.06a4.5 4.5 0 006.364 6.365l3.129-3.129m5.614-5.615l1.757-1.757a4.5 4.5 0 00-6.364-6.365l-3.129 3.129m-2.95 7.827l7.071-7.071" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-rose-700 leading-tight">
                {stats?.unlinked_chats?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-rose-600/70 font-medium">Unlinked</div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mt-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                activeTab === tab.id
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'browse' && <BrowseTab stats={stats} />}
        {activeTab === 'vector-search' && <VectorSearchTab stats={stats} onImport={handleScan} isImporting={isScanning} />}
      </div>
    </div>
  )
}
